import type { ReturnedPortfolio } from '../components/Create-Portfolio/types';

export const EMPTY_PUBLIC_PORTFOLIO: ReturnedPortfolio = {
  overview: '',
  projects: [],
  references: [],
  languages: [],
  certifications: [],
  organizations: [],
  accomplishments: [],
  workCulture: [],
};

export function normalizePublicPortfolio(portfolio: unknown): ReturnedPortfolio {
  if (!portfolio || typeof portfolio !== 'object') {
    return { ...EMPTY_PUBLIC_PORTFOLIO };
  }

  const row = portfolio as Record<string, unknown>;

  return {
    overview: String(row.overview ?? ''),
    projects: Array.isArray(row.projects) ? row.projects : [],
    references: Array.isArray(row.references) ? row.references : [],
    languages: Array.isArray(row.languages) ? row.languages : [],
    certifications: Array.isArray(row.certifications) ? row.certifications : [],
    organizations: Array.isArray(row.organizations) ? row.organizations : [],
    accomplishments: Array.isArray(row.accomplishments) ? row.accomplishments : [],
    workCulture: Array.isArray(row.workCulture)
      ? row.workCulture
      : Array.isArray(row.work_culture)
        ? (row.work_culture as string[])
        : [],
    isOverviewEnabled:
      typeof row.isOverviewEnabled === 'boolean'
        ? row.isOverviewEnabled
        : row.is_overview_enabled !== false,
    isProjectsEnabled:
      typeof row.isProjectsEnabled === 'boolean'
        ? row.isProjectsEnabled
        : row.is_projects_enabled !== false,
    isReferencesEnabled:
      typeof row.isReferencesEnabled === 'boolean'
        ? row.isReferencesEnabled
        : row.is_references_enabled !== false,
    isLanguagesEnabled:
      typeof row.isLanguagesEnabled === 'boolean'
        ? row.isLanguagesEnabled
        : row.is_languages_enabled !== false,
    isCertificationsEnabled:
      typeof row.isCertificationsEnabled === 'boolean'
        ? row.isCertificationsEnabled
        : row.is_certifications_enabled !== false,
    isWorkCultureEnabled:
      typeof row.isWorkCultureEnabled === 'boolean'
        ? row.isWorkCultureEnabled
        : row.is_work_culture_enabled !== false,
    isOrganizationsEnabled:
      typeof row.isOrganizationsEnabled === 'boolean'
        ? row.isOrganizationsEnabled
        : row.is_organizations_enabled !== false,
    isAccomplishmentsEnabled:
      typeof row.isAccomplishmentsEnabled === 'boolean'
        ? row.isAccomplishmentsEnabled
        : row.is_accomplishments_enabled !== false,
  };
}

export function isReferenceVerified(reference: Record<string, unknown>): boolean {
  return Boolean(reference.isReferenceConfirmed || reference.is_verified || reference.verified_at);
}

export function verifiedReferencesOnly(
  references: unknown[] | undefined
): Record<string, unknown>[] {
  if (!Array.isArray(references)) return [];
  return references.filter((ref) => {
    if (!ref || typeof ref !== 'object') return false;
    return isReferenceVerified(ref as Record<string, unknown>);
  }) as Record<string, unknown>[];
}

export interface ReferenceVerificationAnswers {
  relationship_summary?: string;
  strengths?: string;
  concerns?: string;
  worked_together?: boolean;
  recommend_hire?: boolean;
}

export function parseReferenceVerificationAnswers(
  raw: unknown
): ReferenceVerificationAnswers | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as ReferenceVerificationAnswers;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') {
    return raw as ReferenceVerificationAnswers;
  }
  return null;
}

export function normalizeSkillsList(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map((item) => (item == null ? '' : String(item).trim())).filter(Boolean);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text) as unknown;
        if (Array.isArray(parsed)) return normalizeSkillsList(parsed);
      } catch {
        /* fall through */
      }
    }
    if (text.includes(',')) {
      return text
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    }
    return [text];
  }
  return [];
}

export function parseProfessionalLinksFromUser(
  user: Record<string, unknown> | null | undefined
): string[] {
  if (!user) return [];
  const links: string[] = [];
  const linkedin = String(user.linkedin ?? '').trim();
  if (linkedin) links.push(linkedin);

  const rawOther = user.otherSocial ?? user.other_social;
  if (Array.isArray(rawOther)) {
    rawOther.forEach((entry) => {
      const value = String(entry ?? '').trim();
      if (value) links.push(value);
    });
  } else if (typeof rawOther === 'string' && rawOther.trim()) {
    try {
      const parsed = JSON.parse(rawOther) as unknown;
      if (Array.isArray(parsed)) {
        parsed.forEach((entry) => {
          const value = String(entry ?? '').trim();
          if (value) links.push(value);
        });
      } else {
        links.push(rawOther.trim());
      }
    } catch {
      links.push(rawOther.trim());
    }
  }

  return Array.from(new Set(links));
}
